import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import axios from '../api/axiosInstance';
import './EquipmentProfilePage.css'; 
import './ProfileWorkspaceTheme.css';
import { isAuthenticated as userIsAuthenticated, getUserRoles } from '../auth/cookieUtils';
import { apiFileUrl } from '../config/runtimeConfig';

const resolveFileUrl = apiFileUrl;



 // Default blank cost object for clean UI
 const emptyCost = {
    inspectionCost: '',
    scaffoldCost: '',
    cleaningCost: '',
    repairCost: '',
    notes: ''
  };


const EquipmentProfilePage = () => {
    const { equipmentId } = useParams();
    const location = useLocation();
  const [profile, setProfile] = useState(null);
  const [profileError, setProfileError] = useState('');
  const [expandedImage, setExpandedImage] = useState(null);
  const [showCostModal, setShowCostModal] = useState(false);
const [costLoading, setCostLoading] = useState(false);
const [inspectionCost, setInspectionCost] = useState(emptyCost);
const isAuthenticated = userIsAuthenticated();
const roles = getUserRoles();
const elevatedPrivileges = isAuthenticated && !roles.includes('USER');
const [selectedActionItem, setSelectedActionItem] = useState(null);
const [showActionItemModal, setShowActionItemModal] = useState(false);
const [showAccessNotesModal, setShowAccessNotesModal] = useState(false);
const [accessNotes, setAccessNotes] = useState({
  internalAccess: '',
  externalAccess: '',
  utAccess: ''
});
const [lastInServiceDate, setLastInServiceDate] = useState(null);

const [selectedRecordId, setSelectedRecordId] = useState(null);

// memoized docs for the selected record
const selectedRecordDocs = React.useMemo(() => {
  if (!selectedRecordId) return [];
  return (profile?.inspectionRecordDocuments || []).filter(
    d => String(d.inspectionRecordId) === String(selectedRecordId)
  );
}, [profile, selectedRecordId]);


const inspectionFields = [
    { label: "Asset Signage (Present / Legible)", okKey: "signageOk", notesKey: "signageNotes" },
    { label: "Check for signs of leakage", okKey: "leakageOk", notesKey: "leakageNotes" },
    { label: "Check for shell distortion & excessive corrosion", okKey: "shellCorrosionOk", notesKey: "shellCorrosionNotes" },
    { label: "Check condition of coating: (corrosion, scaling, pitting)", okKey: "coatingOk", notesKey: "coatingNotes" },
    { label: "Signs of settlement / sagging", okKey: "settlingOk", notesKey: "settlingNotes" },
    { label: "Check condition of Foundation: (cracks, spalling, exposed rebar)", okKey: "foundationOk", notesKey: "foundationNotes" },
    { label: "Insulation System: (Damage/penetrations...)", okKey: "insulationOk", notesKey: "insulationNotes" },
    { label: "Check condition of overpressure protection / relief devices", okKey: "overpressureOk", notesKey: "overpressureNotes" },
    { label: "Check condition of Secondary Containment", okKey: "containmentOk", notesKey: "containmentNotes" },
    { label: "Check condition of grounding cable", okKey: "groundingOk", notesKey: "groundingNotes" },
    { label: "Condition of ladders, handrails & platforms", okKey: "laddersOk", notesKey: "laddersNotes" }
  ];
  

  const [routineInspection, setRoutineInspection] = useState({
    signageOk: false,
    signageNotes: '',
    leakageOk: false,
    leakageNotes: '',
    shellCorrosionOk: false,
    shellCorrosionNotes: '',
    coatingOk: false,
    coatingNotes: '',
    settlingOk: false,
    settlingNotes: '',
    foundationOk: false,
    foundationNotes: '',
    insulationOk: false,
    insulationNotes: '',
    overpressureOk: false,
    overpressureNotes: '',
    containmentOk: false,
    containmentNotes: '',
    groundingOk: false,
    groundingNotes: '',
    laddersOk: false,
    laddersNotes: '',
    inspector: ''
  });
  
  
  
const navigate = useNavigate();

const handleValveRowClick = (valveId) => {
  if (!valveId) return;
  navigate(`/valves/${valveId}/profile`);
};

  
  const handleRecordClick = async (recordId) => {
  setSelectedRecordId(recordId);
  setShowCostModal(true);

  // Only elevated users should load/view costs
  if (!elevatedPrivileges) {
    setInspectionCost(emptyCost);
    setCostLoading(false);
    return;
  }

  setCostLoading(true);

  try {
    const res = await axios.get(`/api/inspection-costs/equipment/${equipmentId}`);
    const costs = res.data || [];
    const match = costs.find((c) => c.inspectionRecordId === recordId);

    if (match) {
      setInspectionCost({
        inspectionCost: match.inspectionCost ?? '',
        scaffoldCost: match.scaffoldCost ?? '',
        cleaningCost: match.cleaningCost ?? '',
        repairCost: match.repairCost ?? '',
        notes: match.notes ?? ''
      });
    } else {
      setInspectionCost({
        ...emptyCost,
        notes: 'No cost data found for this inspection record.'
      });
    }
  } catch (e) {
    setInspectionCost({ ...emptyCost, notes: 'Failed to load costs.' });
  } finally {
    setCostLoading(false);
  }
};

  const formatDate = (val) => {
  if (!val) return '';
  // backend sends LocalDate as "YYYY-MM-DD"
  if (typeof val === 'string') return val;
  try {
    return new Date(val).toISOString().slice(0, 10);
  } catch {
    return String(val);
  }
};

const displayNext = (dateVal, overrideVal) => {
  const override = (overrideVal ?? '').toString().trim();
  if (override) return override;               // "As Available", etc.
  const date = formatDate(dateVal);
  return date || '—';
};


const sortByDateDesc = (items, dateKey) => {
  return [...(items || [])].sort((a, b) => {
    const dateA = a?.[dateKey] ? new Date(a[dateKey]).getTime() : 0;
    const dateB = b?.[dateKey] ? new Date(b[dateKey]).getTime() : 0;

    return dateB - dateA;
  });
};

  useEffect(() => {
    setProfileError('');
    axios.get(`/api/equipment/${equipmentId}/profile`)
      .then((res) => {
        setProfile(res.data);
        setAccessNotes({
          internalAccess: res.data?.accessNotes?.internalAccess || '',
          externalAccess: res.data?.accessNotes?.externalAccess || '',
          utAccess: res.data?.accessNotes?.utAccess || ''
        });
      })
      .catch((err) => {
        console.error('Error fetching equipment profile:', err);
        setProfileError('This equipment profile could not be loaded. Check the link and try again.');
      });

    if (!isAuthenticated) return;

      axios.get(`/api/routine-inspections/${equipmentId}/last-updated`)
      .then((res) => setLastInServiceDate(res.data))
      .catch(() => setLastInServiceDate(null));

      // Fetch the routine inspection record to prefill textboxes, but not checkboxes
axios.get(`/api/routine-inspections/${equipmentId}`)
.then(res => {
  const data = res.data || {};
  setRoutineInspection((prev) => ({
    ...prev,
    inspector:  '',
    signageNotes: data.signageNotes || '',
    leakageNotes: data.leakageNotes || '',
    shellCorrosionNotes: data.shellCorrosionNotes || '',
    coatingNotes: data.coatingNotes || '',
    settlingNotes: data.settlingNotes || '',
    foundationNotes: data.foundationNotes || '',
    insulationNotes: data.insulationNotes || '',
    overpressureNotes: data.overpressureNotes || '',
    containmentNotes: data.containmentNotes || '',
    groundingNotes: data.groundingNotes || '',
    laddersNotes: data.laddersNotes || '',
    // Explicitly set all checkboxes to false!
    signageOk: false,
    leakageOk: false,
    shellCorrosionOk: false,
    coatingOk: false,
    settlingOk: false,
    foundationOk: false,
    insulationOk: false,
    overpressureOk: false,
    containmentOk: false,
    groundingOk: false,
    laddersOk: false
  }));
})
.catch(() => {
  // Leave as is if not found
});

  }, [equipmentId, isAuthenticated]);
  

  const formatLabel = (label) =>
    label.replace(/([A-Z])/g, ' $1').replace(/^./, (str) => str.toUpperCase());

  if (profileError) {
    return (
      <div className="equipment-profile-page odin-profile-page">
        <div className="profile-loading-state" role="alert">
          <h1>Unable to load equipment profile</h1>
          <p>{profileError}</p>
          <button type="button" className="back-btn" onClick={() => window.location.reload()}>
            Try again
          </button>
        </div>
      </div>
    );
  }

  if (!profile || !profile.equipmentDescription) {
    return (
      <div className="equipment-profile-page odin-profile-page">
        <div className="profile-loading-state" role="status">
          <span className="profile-loading-indicator" aria-hidden="true" />
          <h1>Loading equipment profile</h1>
          <p>Retrieving the asset record and inspection history.</p>
        </div>
      </div>
    );
  }

  const equipmentLabel =
    profile.equipmentDescription.name ||
    profile.equipmentDescription.equipmentName ||
    profile.equipmentDescription.equipmentNumber ||
    'Asset record';

  const fileSections = [
    { label: 'Design Documents', docs: profile.designDocuments, type: 'doc' },
    { label: 'Inspection Record Documents', docs: profile.inspectionRecordDocuments, type: 'doc' },
    { label: 'Vent & Overflow Documents', docs: profile.ventOverflowDocuments, type: 'doc' },
    { label: 'Inspection Protocol Documents', docs: profile.inspectionProtocolDocuments, type: 'doc' },
    { label: 'Tmin Documents', docs: profile.tminCalculationDocuments, type: 'doc' },
    { label: 'Additional Photos', docs: profile.additionalPhotos, type: 'photo' }
  ];

  return (
    <>
      <div className="equipment-profile-page odin-profile-page">
      <div className="profile-header-bar">
  <div className="profile-header-left">
    {isAuthenticated && (
      <button
  className="back-btn"
  onClick={() => {
    if (location.state?.fromAssetsDatabase) {
      navigate('/assets_database', {
        state: {
          restoreFilters: location.state.assetsFilters,
        },
      });
    } else {
      navigate(-1);
    }
  }}
>
  ← Back
</button>
    )}
  </div>

  <div className="profile-header-center">
    <div className="title-photo-wrapper">
      <div className="profile-title-copy">
        <span className="profile-page-eyebrow">Asset record</span>
        <h1 className="slideout-title">Equipment Profile</h1>
        <span className="profile-page-subtitle">{equipmentLabel}</span>
      </div>
    </div>
  </div>

  <div className="profile-header-right">
   
      {elevatedPrivileges && <button type="button" className="access-notes-btn" onClick={() => setShowAccessNotesModal(true)}>
        📝 Access Notes
      </button>}
    
    <button type="button" className="print-btn" onClick={() => window.print()}>
      🖨️ Print
    </button>
  </div>
</div>






        <main className="slide-content profile-workspace-content">
          <div className="header-with-photo">
            {profile?.designInformation?.photoPath && (
              <div className="design-photo-inline">
                <img
                  src={resolveFileUrl(profile.designInformation.photoPath)}
                  alt="Design Preview"
                  onClick={() => setExpandedImage(resolveFileUrl(profile.designInformation.photoPath))}
                  className="design-preview-img"
                />
              </div>
            )}
          </div>

          {profile?.equipmentDescription?.outOfService && (
  <div className="out-of-service-banner">
    🚫 OUT OF SERVICE 🚫
  </div>
)}

          <div className="slideout-grid">
            

            <div className="profile-section-card print-two-column">
  <h3 className="section-title">Equipment Description</h3>

  {(() => {
    const d = profile.equipmentDescription || {};

    const SKIP_KEYS = new Set([
      'id', 'equipmentId', 'areaId',
      // don’t show raw fields — we’ll show the combined display values instead
      'nextExternalInspection', 'nextInternalInspection', 'nextUtInspection',
      'nextExternalOverride', 'nextInternalOverride', 'nextUtOverride',
    ]);

    // Show everything else normally
    const normalLines = Object.entries(d)
      .filter(([key]) => !SKIP_KEYS.has(key))
      .map(([key, value]) => (
        <p key={key}>
          <strong>{formatLabel(key)}:</strong>{' '}
          {typeof value === 'boolean' ? (value ? 'Yes' : 'No') : (value ?? '')}
        </p>
      ));

    return (
      <>
        {normalLines}

        <p>
          <strong>Next External Inspection:</strong>{' '}
          {displayNext(d.nextExternalInspection, d.nextExternalOverride)}
        </p>
        <p>
          <strong>Next Internal Inspection:</strong>{' '}
          {displayNext(d.nextInternalInspection, d.nextInternalOverride)}
        </p>
        <p>
          <strong>Next UT Inspection:</strong>{' '}
          {displayNext(d.nextUtInspection, d.nextUtOverride)}
        </p>
      </>
    );
  })()}
</div>

            <div className="profile-section-card print-two-column">
  <h3 className="section-title">Design Information</h3>
  {Object.entries(profile.designInformation || {}).map(([key, value]) =>
    key !== 'photoPath' && (
      <p key={key}>
        <strong>{formatLabel(key)}:</strong>{' '}
        {typeof value === 'boolean' ? (value ? 'Yes' : 'No') : (value ?? '')}
      </p>
    )
  )}
</div>
          
            <div className="profile-section-card full-width">
              <h3 className="section-title">Covered Processes</h3>
              {profile.coveredProcesses && profile.coveredProcesses.length > 0 ? (
               <ul className="covered-process-list">
                {profile.coveredProcesses.map((cp, idx) => (
                <li key={idx}>{cp.processName}</li>
              ))}
              </ul>
            ) : (
              <p>No covered processes recorded.</p>
            )}
          </div>
            
            
            <div className="profile-section-card files-section full-width">
              <h3 className="section-title">Files</h3>
              {fileSections.map(({ label, docs, type }, index) => (
                <div className="file-readonly-wrapper" key={index}>
                  <div className="file-label">{label}</div>
                  {docs?.length > 0 && (
                    <div className={type === 'photo' ? 'file-preview-grid' : 'file-list'}>
                      {docs.map((doc, idx) =>
                        type === 'photo' ? (
                          <div className="thumbnail" key={idx}>
                            <img
                              src={resolveFileUrl(doc.filePath)}
                              alt={`Additional evidence ${idx + 1}`}
                              onClick={() => setExpandedImage(resolveFileUrl(doc.filePath))}
                              className="file-thumbnail-img"
                            />
                          </div>
                        ) : (
                          <div className="file-item" key={idx}>
                            <a
                              className="file-name"
                              href={resolveFileUrl(doc.filePath)}
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              {doc.fileName?.trim() || doc.filePath?.split('/').pop() || `Document ${idx + 1}`}
                            </a>
                          </div>
                        )
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
             
              
            <div className="profile-section-card full-width">
              <h3 className="section-title">Inspection Records</h3>
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Company</th>
                    <th>Inspector</th>
                    <th>Type</th>
                    <th>Job Scope</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                {sortByDateDesc(profile.inspectionRecords, 'date').map((record, idx) => (
  <tr
  key={idx}
  onClick={() => handleRecordClick(record.id)}
  title="Click to view inspection report"
  style={{ cursor: 'pointer' }}
>

                      <td>{record.date}</td>
                      <td>{record.company}</td>
                      <td>{record.inspector}</td>
                      <td>{record.type}</td>
                      <td>{record.jobScope}</td>
                      <td>{record.status}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
                  
                  
            <div className="profile-section-card full-width">
  <h3 className="section-title">Action Items</h3>
  <table className="data-table">
    <thead>
      <tr>
        <th>Date</th>
        <th>Action Text</th>
        <th>Category</th>
        <th>Type</th>
        <th>Priority</th>
        <th>Status</th>
        <th>Completed</th>
        <th>Actions</th>
      </tr>
    </thead>
    <tbody>
      {sortByDateDesc(profile.actionItems, 'dueDate').map((item, idx) => (
        <tr
        key={idx}
        onClick={() => {
          if (elevatedPrivileges) {
            setSelectedActionItem(item);
            setShowActionItemModal(true);
          }
        }}
        title={elevatedPrivileges ? 'Click to view/edit notes' : ''}
        style={{
          cursor: elevatedPrivileges ? 'pointer' : 'default',
          opacity: elevatedPrivileges ? 1 : 0.6
        }}
      >
      
          <td>{item.dueDate}</td>
          <td>{item.actionText}</td>
          <td>{item.category}</td>
          <td>{item.type}</td>
          <td>{item.priority}</td>
          <td>{item.status}</td>
          <td>{item.completed ? 'Yes' : 'No'}</td>
          <td>
          {elevatedPrivileges && !item.completed && (
  <button
    className="mark-complete-btn"
    onClick={async (event) => {
      event.stopPropagation();
      try {
        await axios.put(`/api/action-items/complete/${item.id}`);
        const res = await axios.get(`/api/equipment/${equipmentId}/profile`);
        setProfile(res.data);
      } catch (err) {
        alert('Failed to mark as complete');
        console.error(err);
      }
    }}
  >
    Mark Complete
  </button>
)}
</td>

        </tr>
      ))}
    </tbody>
  </table>
</div>


<div className="profile-section-card full-width">
  <h3 className="section-title">Valves</h3>

  <table className="data-table">
    <thead>
      <tr>
        <th>Tag Number</th>
        <th>Set Pressure</th>
        <th>Test Freq</th>
        <th>Next Test</th>
        <th>Next Repair</th>
      </tr>
    </thead>

    <tbody>
      {(profile.valves || []).map((v) => (
        <tr
          key={v.id}
          onClick={() => handleValveRowClick(v.id)}
          style={{ cursor: 'pointer' }}
          title="Open valve profile"
        >
          <td>{v.valveTagNumber || ''}</td>
          <td>{v.setPressure || ''}</td>
          <td>{v.testFrequency || ''}</td>
          <td>{v.nextTestDate || ''}</td>
          <td>{v.nextRepairReplacement || ''}</td>
        </tr>
      ))}

      {(profile.valves || []).length === 0 && (
        <tr>
          <td colSpan={5} style={{ color: '#bbb' }}>
            No valves added yet.
          </td>
        </tr>
      )}
    </tbody>
  </table>
</div>

{isAuthenticated && (
  
<div className="profile-section-card full-width">
  <h3 className="section-title">Routine In-Service Inspection</h3>

  <div className="routine-inspection-wrapper">
    <div className="question-wrapper">
      <p className="inspection-question">
        Does this equipment require a routine in-service inspection?
      </p>
    </div>

    <div className="radio-options">
      <label style={{ color: 'white' }}>
        <input
          type="radio"
          name="requiresRoutineInspection"
          value="true"
          checked={profile.equipmentDescription?.requiresRoutineInspection === true}
          onChange={() =>
            setProfile((prev) => ({
              ...prev,
              equipmentDescription: {
                ...prev.equipmentDescription,
                requiresRoutineInspection: true,
              },
            }))
          }
        />
        Yes
      </label>

      <label style={{ marginLeft: '1.5rem', color: 'white' }}>
        <input
          type="radio"
          name="requiresRoutineInspection"
          value="false"
          checked={profile.equipmentDescription?.requiresRoutineInspection === false}
          onChange={() =>
            setProfile((prev) => ({
              ...prev,
              equipmentDescription: {
                ...prev.equipmentDescription,
                requiresRoutineInspection: false,
              },
            }))
          }
        />
        No
      </label>
    </div>

    {profile.equipmentDescription?.requiresRoutineInspection === true && (
      <>
        {/* Last In-Service Inspection Date */}
        <div className="last-inspection-date" style={{ color: 'white', marginTop: '1rem' }}>
  <strong>Last In-Service Inspection:</strong>{' '}
  {lastInServiceDate ? (
    new Date(lastInServiceDate).toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    })
  ) : (
    'No Previous In-Service Inspections'
  )}
</div>


        {/* Inspector Input */}
        <div className="inspector-input-group">
          <label htmlFor="inspector" style={{ color: 'white', fontWeight: 'bold', display: 'block', marginTop: '1rem' }}>
            Inspector:
          </label>
          <input
            type="text"
            id="inspector"
            value={routineInspection.inspector}
            onChange={(e) =>
              setRoutineInspection((prev) => ({
                ...prev,
                inspector: e.target.value,
              }))
            }
            className="inspector-text-input"
            placeholder="Enter inspector name"
            style={{
              width: '100%',
              padding: '0.5rem',
              marginBottom: '1rem',
              borderRadius: '4px',
              border: '1px solid #ccc',
              backgroundColor: '#1f1f1f',
              color: 'white',
            }}
          />
        </div>

        {/* Checklist */}
        <div className="routine-inspection-questions">
          {inspectionFields.map(({ label, okKey, notesKey }) => (
            <div key={okKey} className="inspection-item">
              <p className="inspection-question-label">{label}</p>
              <label style={{ color: 'white' }}>
                <input
                  type="checkbox"
                  checked={routineInspection[okKey]}
                  onChange={() =>
                    setRoutineInspection((prev) => ({
                      ...prev,
                      [okKey]: !prev[okKey],
                    }))
                  }
                />
                Completed
              </label>
              <textarea
                placeholder="Abnormal conditions (if any)"
                value={routineInspection[notesKey]}
                onChange={(e) =>
                  setRoutineInspection((prev) => ({
                    ...prev,
                    [notesKey]: e.target.value,
                  }))
                }
                className="abnormal-notes-textarea"
              />
            </div>
          ))}
        </div>
      </>
    )}

    {/* Save button always at the bottom */}
    <div style={{ marginTop: '1rem' }}>
      <button
        className="save-btn"
        onClick={async () => {
          try {
            // Update requiresRoutineInspection boolean
            await axios.put(`/api/equipment/descriptions/${equipmentId}/routine-flag`, profile.equipmentDescription.requiresRoutineInspection);


            // Update routine inspection fields
            await axios.put(`/api/routine-inspections/${equipmentId}`, {
              ...routineInspection,
            });

            // Refresh profile
            const res = await axios.get(`/api/equipment/${equipmentId}/profile`);
            setProfile(res.data);

            alert('Routine inspection data saved successfully!');
          } catch (err) {
            alert('Failed to save routine inspection data.');
            console.error(err);
          }
        }}
      >
        Save
      </button>
    </div>
  </div>
</div>

)}



          </div>
        </main>
      </div>

      {expandedImage && (
        <div className="image-modal profile-theme-modal" onClick={() => setExpandedImage(null)}>
          <div className="image-modal-content" onClick={(e) => e.stopPropagation()}>
            <button className="close-modal" onClick={() => setExpandedImage(null)}>✕</button>
            <img src={expandedImage} alt="Expanded Preview" />
          </div>
        </div>
      )}
      {showCostModal && (
  <div
    className="modal-overlay profile-theme-modal"
    onClick={() => { setShowCostModal(false); setSelectedRecordId(null); }}
  >
    <div className="modal-content" onClick={e => e.stopPropagation()}>
      <button
        className="close-modal"
        onClick={() => { setShowCostModal(false); setSelectedRecordId(null); }}
      >
        ✕
      </button>
      <h3>Inspection Details</h3>
      {costLoading ? (
        <p>Loading...</p>
      ) : (
        <>
          {elevatedPrivileges && (
  <table className="cost-table">
    <tbody>
      <tr><th>Inspection Cost</th><td>{inspectionCost.inspectionCost || '-'}</td></tr>
      <tr><th>Scaffold Cost</th><td>{inspectionCost.scaffoldCost || '-'}</td></tr>
      <tr><th>Cleaning Cost</th><td>{inspectionCost.cleaningCost || '-'}</td></tr>
      <tr><th>Repair Cost</th><td>{inspectionCost.repairCost || '-'}</td></tr>
      <tr><th>Notes</th><td>{inspectionCost.notes || '-'}</td></tr>
    </tbody>
  </table>
)}

          {/* Documents for this inspection record */}
          <hr className="modal-divider" />
          <h4 style={{ marginTop: 12 }}>Inspection Documents</h4>
          {selectedRecordDocs.length > 0 ? (
            <div className="file-list" style={{ marginTop: 8 }}>
              {selectedRecordDocs.map((doc, idx) => (
                <div className="file-item" key={doc.id ?? idx}>
                  <a
                    className="file-name"
                    href={resolveFileUrl(doc.filePath)}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {doc.fileName?.trim() || doc.filePath?.split('/').pop() || `Document ${idx + 1}`}
                  </a>
                </div>
              ))}
            </div>
          ) : (
            <div className="hint" style={{ marginTop: 8 }}>
              No documents uploaded for this inspection record.
            </div>
          )}
        </>
      )}
    </div>
  </div>
)}

{showActionItemModal && selectedActionItem && (
  <div className="modal-overlay profile-theme-modal" onClick={() => setShowActionItemModal(false)}>
    <div className="modal-content" onClick={(e) => e.stopPropagation()}>
      <button className="close-modal" onClick={() => setShowActionItemModal(false)}>✕</button>
      <h3>Action Item Notes</h3>

      <div>
        <label>
          <strong>Action Text:</strong>
          <div>{selectedActionItem.actionText}</div>
        </label>
        <label>
          <strong>Notes:</strong>
          <textarea
            value={selectedActionItem.notes || ''}
            onChange={(e) =>
              setSelectedActionItem({ ...selectedActionItem, notes: e.target.value })
            }
            rows={5}
            style={{ width: '100%', marginTop: '0.5rem' }}
          />
        </label>
        <button
          className="save-btn"
          style={{ marginTop: '1rem' }}
          onClick={async () => {
            try {
              await axios.put(`/api/action-items/${selectedActionItem.id}`, {
                ...selectedActionItem,
              });
              const res = await axios.get(`/api/equipment/${equipmentId}/profile`);
              setProfile(res.data);
              setShowActionItemModal(false);
            } catch (err) {
              alert('Failed to save notes');
              console.error(err);
            }
          }}
        >
          Save
        </button>

      </div>
    </div>
  </div>
)}

{elevatedPrivileges && showAccessNotesModal && (
  <div className="modal-overlay profile-theme-modal" onClick={() => setShowAccessNotesModal(false)}>
    <div className="modal-content" onClick={(e) => e.stopPropagation()}>
      <button className="close-modal" onClick={() => setShowAccessNotesModal(false)}>✕</button>
      <h3>Access Notes</h3>
      <label><strong>Internal Access:</strong></label>
      <textarea
        value={accessNotes.internalAccess || ''}
        onChange={(e) => setAccessNotes({ ...accessNotes, internalAccess: e.target.value })}
        rows={3}
        style={{ width: '100%', marginBottom: '1rem' }}
      />
      <label><strong>External Access:</strong></label>
      <textarea
        value={accessNotes.externalAccess || ''}
        onChange={(e) => setAccessNotes({ ...accessNotes, externalAccess: e.target.value })}
        rows={3}
        style={{ width: '100%', marginBottom: '1rem' }}
      />
      <label><strong>UT Access:</strong></label>
      <textarea
        value={accessNotes.utAccess || ''}
        onChange={(e) => setAccessNotes({ ...accessNotes, utAccess: e.target.value })}
        rows={3}
        style={{ width: '100%', marginBottom: '1rem' }}
      />
      <button
        className="save-btn"
        onClick={async () => {
          try {
            await axios.put(`/api/access-notes/${equipmentId}`, accessNotes);
            const res = await axios.get(`/api/equipment/${equipmentId}/profile`);
            setProfile(res.data);
            setShowAccessNotesModal(false);
          } catch (err) {
            alert('Failed to save access notes');
            console.error(err);
          }
        }}
      >
        Save Notes
      </button>
    </div>
  </div>
)}

</>

  );
};

export default EquipmentProfilePage;
